import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import Stepper from 'components/book/Stepper'

import Window from 'components/dialog/Window'
import Loading from 'components/system/Loading'

import projectSelectors from 'store/selectors/project'
import bookingSelectors from 'store/selectors/booking'
import routerSelectors from 'store/selectors/router'
import routerActions from 'store/modules/router'
import bookingActions from 'store/modules/booking'
import networkActions from 'store/modules/network'

import useMediaQuerySize from 'components/hooks/useMediaQuerySize'

import {
  BOOKING_STEPS,
} from 'settings'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0),
    width: '100%',
  },
  alert: {
    width: '100%',
    position: 'fixed',
    zIndex: 1000,
  },
  contentPadding: ({withAlert}) => ({
    paddingTop: withAlert ? '107px' : '30px',
    padding: theme.spacing(4),
  }),
  stepper: {
    display: 'flex',
    flexDirection: 'row',
  },
}))

const isDifferentBookingForm = (reservationRedirect, project, bookingform) => {
  if(!reservationRedirect || !project || !bookingform) return false
  const sameProject = reservationRedirect.project == project || reservationRedirect.projectUrl == project
  const sameBookingform = reservationRedirect.bookingform == bookingform || reservationRedirect.bookingformUrl == bookingform
  return sameProject && sameBookingform ? false : true
}

const BookingFormLayout = ({
  children,
}) => {
  const screenSize = useMediaQuerySize()
  const dispatch = useDispatch()
  const store = useStore()

  // did they have a reservation on a bookingform that is different to the one they are looking at?
  // this will show a modal to cancel their current booking before they can see the new calendar
  const [ reservationMismatch, setReservationMismatch ] = useState(null)
  const [ statusLoaded, setStatusLoaded ] = useState(false)

  const {
    project,
    bookingform,
  } = useSelector(routerSelectors.params)

  const stripeConnect = useSelector(projectSelectors.currentProjectStripeConnect)

  const classes = useStyles({
    withAlert: stripeConnect ? false : true,
  })

  const segments = useSelector(routerSelectors.segments)
  const reservationRedirect = useSelector(bookingSelectors.reservationRedirect)
  const slot = useSelector(bookingSelectors.slot)
  const step = segments[segments.length-1]
  const setStep = useCallback((step, params) => {
    const routeParams = {
      project,
      bookingform,
      ...params
    }
    if(step == 'calendar' && !routeParams.date) {
      routeParams.date = slot ? slot.date : 'today'
    }
    dispatch(routerActions.navigateTo(`book.form.${step}`, routeParams))
  }, [
    project,
    bookingform,
    slot,
  ])

  const onCancelCurrentBooking = useCallback(async () => {
    const result = await dispatch(bookingActions.cancelBooking())
    if(result) setReservationMismatch(null)
  }, [])

  const continueCurrentBooking = useCallback(() => {
    if(!reservationRedirect) return
    setReservationMismatch(null)
    dispatch(routerActions.navigateTo(reservationRedirect.name, reservationRedirect.params))  
  }, [
    reservationRedirect,
  ])

  const stepData = useMemo(() => {
    return BOOKING_STEPS.find(s => s.id == step)
  }, [
    step,
  ])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [
    step,
  ])

  // the main public booking initialiser
  // this will check the reserved booking token
  // and load everything from the api
  // it will then check the project,bookingform reservation ids
  // to see if we need to show a modal
  useEffect(() => {
    const doAsync = async () => {
      const result = await dispatch(bookingActions.initialise())
      if(result && project && bookingform) {
        if(isDifferentBookingForm(result, project, bookingform)) {
          setReservationMismatch(true)
          setStatusLoaded(true)
        }
        else {
          setTimeout(async () => {
            await dispatch(routerActions.navigateTo(result.name, result.params))  
            setStatusLoaded(true)
          }, 100) 
        }
      }
      else {
        setStatusLoaded(true)
      } 
    }
    doAsync()
  }, [])


  // reacts to changes in project, bookingform
  // check they are the same as the reservation
  // if not then show modal
  useEffect(() => {
    if(isDifferentBookingForm(reservationRedirect, project, bookingform)) {
      setReservationMismatch(true)
    }
  }, [
    project,
    bookingform,
  ])

  if(!statusLoaded) {
    return (
      <Loading />
    )
  }

  return (
    <div className={ classes.root }>
      {
        !stripeConnect && (
          <Alert
            className={ classes.alert }
            severity="error"
          >
            <AlertTitle>Payment Not Configured</AlertTitle>
            This project has not connected their Stripe account and so we can't take any payments for you until they have connected their Stripe account.
          </Alert>
        )
      }
      <div className={ classes.contentPadding }>
        {
          stepData && (
            screenSize == 'large' ? (
              <div className={ classes.stepper }>
                <Stepper
                  value={ step }
                  onChange={ setStep }
                />
              </div>
            ) : (
              <Typography variant="h6">
                { stepData.title }
              </Typography>
            )
          )
        }
        {
          !reservationMismatch && (
            <div className={ classes.contentActual }>
              { children }
            </div>
          )
        }
      </div>
      {
        reservationMismatch && (
          <Window
            open
            title="Existing booking reservation found"
            size="sm"
            withCancel
            submitTitle="Continue with booking"
            cancelTitle="Discard existing booking"
            onSubmit={ continueCurrentBooking }
            onCancel={ onCancelCurrentBooking }
          >
            <Typography gutterBottom>
              You have an active reservation on another booking calendar.
            </Typography>
            <Typography gutterBottom>
              You can cancel the existing booking and view this calendar
            </Typography>
            <Typography gutterBottom>
              Or you can continue with your existing booking.
            </Typography>
          </Window>
        )
      }
    </div>
    
  )
}

export default BookingFormLayout